import "leaflet/dist/leaflet.css";
import "./styles.css";
import { useState, useLayoutEffect, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import LZString from "lz-string";
import Map from "./Map";
import { getIndexData } from "../../api/FlaskAPI";
import {
  setDatePickerFrom,
  setDatePickerTo,
  setAreaTiles,
  clearAllMapState,
  clearDates,
  setDownloadLink,
  setFileContent,
  setUploaderFlag,
  setShowOverlay,
} from "../../reducers/MapSlice";
import { setCurrentIndexId } from "../../reducers/MonitoringSlice";
import { setCategories } from "../../reducers/CategoriesSlice";
import { setUser } from "../../reducers/userSlice";
import SignInComponent from "../../components/Sign/SignComponent";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from "rsuite";
import { CustomProvider } from "rsuite";
import arEG from "rsuite/locales/ar_EG";
import useApi from "../../hooks/useApi";
import { useMemo } from "react";
import { toast } from "react-toastify";
import CoordinatesInput from "../../components/CoordinatesInput";
import NavPageSelector from "../../components/NavPageSelector";
import * as constants from "../../utils/constants";
import { getChartData } from "../../utils/charts_format";
import "react-photo-view/dist/react-photo-view.css";
import MonitoringDialog from "../Monitoring/MonitoringDialog";
import LandDegradationDialog from "../LandDegradation/LandDegradationDialog";
import IndexInfoDialog from "../../components/IndexInfoDialog";
import AddTiffDialog from "../../components/AddTiffDialog";
import { url } from "../../api/axios_client";
import downloadIcon from "../../assets/images/download-icon.png";
import axios from "../../api/axios_client";
import { useAdmin } from "../../hooks/useAdmin";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import Stripe from "./Stripe";
import { CircularProgress, Typography, Box, useMediaQuery, useTheme } from "@mui/material";



// Right-side panel for map controls + image info
function RightSidePanel({ areas, showOverlay, dispatch, t, fetchTiffImage, tiffLoading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isBelow1000 = useMediaQuery('(max-width:1000px)');
  const isTinyMobile = useMediaQuery('(max-width:387px)');
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const handleRemoveArea = () => {
    localStorage.removeItem('mapFileContent');
    dispatch(clearAllMapState());
    dispatch(setUploaderFlag(false));
    window.dispatchEvent(new Event('clearDrawnShapes'));
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: isTinyMobile ? 245 : (isBelow1000 ? 205 : 110),
        right: isArabic ? 'auto' : (isBelow1000 ? '0.8em' : '5em'),
        left: isArabic ? (isBelow1000 ? '0.8em' : '5em') : 'auto',
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        px: 2,
        borderRadius: "15px",
        backgroundColor: "rgba(255, 255, 255, 0.75)",
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
        zIndex: 100,
        width: "fit-content",
        maxWidth: isMobile ? "70vw" : "280px",
      }}
    >
      {/* Control buttons */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <button className="delete-button" onClick={handleRemoveArea}>
          {t("Map.remove_file")}
        </button>
        <button
          className="delete-button"
          onClick={() => dispatch(setShowOverlay(!showOverlay))}
          style={{ backgroundColor: showOverlay ? '#329ace' : '#666' }}
        >
          {showOverlay ? t("Map.hide_overlay") || "Hide Overlay" : t("Map.show_overlay") || "Show Overlay"}
        </button>
      </Box>

      {/* Image info for areas with tiles */}
      {[...areas].slice(0, 2).map((area, idx) => {
        if (!area.tileUrl && !area.imagename) return null;
        return (
          <Box
            key={`${idx}-${area.imagename}`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              py: 0.5,
              borderTop: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ color: "#333", minWidth: 0, flex: 1, display: 'flex' }}>
              <span style={{ fontWeight: 600, fontSize: "13px", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
                {area.imagename || `Area ${idx + 1}`}
              </span>
            </Box>
            <button
              className="delete-button"
              onClick={() => !tiffLoading && fetchTiffImage(idx)}
              style={{
                cursor: tiffLoading ? "not-allowed" : "pointer",
                opacity: tiffLoading ? 0.6 : 1,
                padding: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "5px",
                width: "32px",
                height: "32px",
              }}
              title="Download TIFF"
            >
              {tiffLoading ? (
                <CircularProgress size={16} style={{ color: "white" }} />
              ) : (
                <img src={downloadIcon} alt="Download" style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }} />
              )}
            </button>
          </Box>
        );
      })}
    </Box>
  );
}

export default function BaseBage() {
  const { index } = useParams();
  const dataKey = index
  const isAdmin = useAdmin();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [landDegradationOpen, setLandDegradationOpen] = useState(false);
  const [indexInfoOpen, setIndexInfoOpen] = useState(false);
  const [addTiffOpen, setAddTiffOpen] = useState(false);
  const [mainIndex, setMainIndex] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [tiffLoading, setTiffLoading] = useState(false); // TIFF loading state

  const handleOpen = () => {
    if (!areas || !areas[0] || !areas[0].coordinates) {
      toast.error("Please select the area you want to be monitored");
    } else {
      setMainIndex(dataKey);
      setOpen(true);
    }
  };
  const handleIndexInfoOpen = () => {
    setIndexInfoOpen(true);
  };
  const handleAddTiffOpen = () => {
    setAddTiffOpen(true);
  };

  const handleClose = () => {
    // dispatch(resetStore());
    setOpen(false);
  };
  const { t, i18n } = useTranslation();
  const loggedInUser = localStorage.getItem(constants.USER);
  const isMobile = window.innerWidth < 700;

  //user states
  const loggedUserState = useSelector((state) => state.userState.user);
  //map states
  const areas = useSelector((state) => state.mapState.areas);
  const datePickerTimeFrom = useSelector(
    (state) => state.mapState.date.datePickerTimeFrom
  );
  const datePickerTimeTo = useSelector(
    (state) => state.mapState.date.datePickerTimeTo
  );
  const showOverlay = useSelector((state) => state.mapState.showOverlay);

  const categories = useSelector((state) => state.categories);

  const indexData = useMemo(() => {
    if (!categories || !Array.isArray(categories) || !dataKey) return null;
    return categories.find((category) => {
      return (
        category.type.toLowerCase() === dataKey.toLowerCase() ||
        (category.category &&
          category.category.toLowerCase() === dataKey.toLowerCase()) ||
        (category.name && category.name.toLowerCase() === dataKey.toLowerCase())
      );
    });
  }, [categories, dataKey]);

  const legendData = useMemo(() => {
    if (!indexData) return null;
    try {
      const legends = indexData.legends.map((legendStr) => {
        const cleanStr = legendStr.replace(/'/g, '"');
        const legendObj = JSON.parse(cleanStr);
        return {
          value: legendObj.label ?? legendObj.value,
          value_ar: legendObj.label_ar,
          color: legendObj.color,
        };
      });
      return {
        title: indexData.name,
        unit: indexData.unit,
        unit_ar: indexData.unit_ar,
        legends,
      };
    } catch (error) {
      console.error("Error parsing legend data:", error);
      return null;
    }
  }, [indexData]);

  const reduxCurrentIndexId = useSelector((state) => state.monitoringState.currentIndexId);
  const currentIndexId = indexData?.id || reduxCurrentIndexId || localStorage.getItem("currentIndexId");

  // Helper function to get date range from state for current index
  const getIndexDateRange = (indexId) => {
    if (!indexId || !categories || !Array.isArray(categories)) return { minDate: null, maxDate: null };
    try {
      const currentIndex = categories.find((category) => category.id === indexId);
      if (currentIndex && currentIndex.available_from && currentIndex.available_to) {
        const minDate = new Date(currentIndex.available_from);
        minDate.setHours(0, 0, 0, 0);
        const maxDate = new Date(currentIndex.available_to);
        maxDate.setHours(23, 59, 59, 999);
        return { minDate, maxDate };
      }
    } catch (error) {
      console.error("Error finding category date range:", error);
    }
    return { minDate: null, maxDate: null };
  };

  // Get the date range for the current index
  const { minDate: indexMinDate, maxDate: indexMaxDate } = getIndexDateRange(currentIndexId);

  // Check if selected dates are within the available range
  const isDateRangeValid = () => {
    if (!datePickerTimeFrom || !datePickerTimeTo) return true; // No dates selected is valid
    if (!indexMinDate || !indexMaxDate) return true; // No range restriction

    const selectedFrom = new Date(datePickerTimeFrom);
    const selectedTo = new Date(datePickerTimeTo);

    return selectedFrom >= indexMinDate && selectedTo <= indexMaxDate;
  };

  const dateRangeError = !isDateRangeValid();

  // Format date range for display
  const formatAvailableDateRange = () => {
    if (indexMinDate && indexMaxDate) {
      const formatDate = (date) => {
        const locale = i18n.language === 'ar' ? 'ar-SA-u-nu-latn' : 'en-US';
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
      };
      return `${t('Map.available_range')}: ${formatDate(indexMinDate)} - ${formatDate(indexMaxDate)}`;
    }
    return "";
  };

  const indexDataRequest = useApi(getIndexData);

  // Synchronize local storage and Redux store with the selected route index
  useEffect(() => {
    if (indexData?.id) {
      if (localStorage.getItem("currentIndexId") !== indexData.id) {
        localStorage.setItem("currentIndexId", indexData.id);
      }
      if (reduxCurrentIndexId !== indexData.id) {
        dispatch(setCurrentIndexId(indexData.id));
      }
    }
  }, [indexData?.id, reduxCurrentIndexId, dispatch]);

  // Handle side-effects on index change (validate selected dates)
  useEffect(() => {
    if (indexData?.id) {
      // Validate selected dates for the new index and clear if they are out of range
      const { minDate, maxDate } = getIndexDateRange(indexData.id);
      if (datePickerTimeFrom && datePickerTimeTo && minDate && maxDate) {
        const selectedFrom = new Date(datePickerTimeFrom);
        const selectedTo = new Date(datePickerTimeTo);
        if (selectedFrom < minDate || selectedTo > maxDate) {
          dispatch(clearDates());
        }
      }
    }
  }, [indexData?.id, dispatch]);

  useEffect(() => {
    dispatch(setUser(JSON.parse(loggedInUser)));

    // Restore map file content if it exists
    const retrievedCompressed = localStorage.getItem("mapFileContent") ?? "";
    const savedMapContent = LZString.decompress(retrievedCompressed);
    if (savedMapContent) {
      dispatch(setFileContent(JSON.parse(savedMapContent)));
      dispatch(setUploaderFlag(true));
    }
  }, [loggedInUser, dispatch]);



  useLayoutEffect(() => {
    return () => {
      // Don't clear the entire map state on unmount
      // Only clear what's necessary
      toast.dismiss();
      const controller = new AbortController();
      controller.abort();
    };
  }, []);

  // Helper function to get error message based on language
  const getErrorMessage = (error, msg = "") => {
    if (error.response?.data) {
      const isArabic = i18n.language === "ar";
      return isArabic
        ? error.response.data.message_ar || error.response.data.message
        : error.response.data.message;
    }
    if (msg) return msg;
    return "An error occurred";
  };

  const updateIndex = async (index) => {
    try {
      const response = await axios.put(`${url}/indexes/${indexData.id}`, {
        index,
      });

      if (response.status === 200) {
        const categoriesStr = localStorage.getItem("categories");
        if (categoriesStr) {
          const storedCategories = JSON.parse(categoriesStr);
          const updated = storedCategories.map((cat) =>
            cat.id === indexData.id ? { ...cat, ...index } : cat
          );
          localStorage.setItem("categories", JSON.stringify(updated));
          dispatch(setCategories(updated));
        }
        toast.success("Index updated successfully");
      }
    } catch (error) {
      console.error("Error updating index:", error);
      const errorMessage = getErrorMessage(error, "Failed to update index");
      toast.error(errorMessage);
    }
  };

  /**
   * fetches the stats of area at index j
   * @param {*} j the index of area
   *
   */

  /**
   * fetch tiles of j area from our server
   *
   * @param  {int} j the index of area
   */
  const getXImage = async (j) => {
    console.log("tiles req begin");

    try {
      const res = await indexDataRequest.request(
        {
          id: currentIndexId,
          request_type: "tiles",
          coordinates: Array.isArray(areas[j].coordinates)
            ? areas[j].coordinates
            : null,
          geo: Array.isArray(areas[j].coordinates)
            ? null
            : JSON.stringify(areas[j].coordinates),
          from_date: datePickerTimeFrom,
          to_date: datePickerTimeTo,
        },
        false
      );

      // Can't connect to server
      if (!res) {
        toast.error(t("request_error"));
        return;
      }

      if (res.status !== 200) {
        const errorMessage = getErrorMessage(
          { response: res },
          t("Map.image_request_error")
        );
        toast.error(errorMessage);
        return;
      }

      // Success
      dispatch(
        setAreaTiles({
          polyId: areas[j].polyId,
          tileUrl: res.data.tile_url_template,
          bbox: res.data.bounds,
          imagename: res.data.imagename,
        })
      );
    } catch (error) {
      console.error("Error fetching tiles:", error);
      const errorMessage = getErrorMessage(error, t("Map.image_request_error"));
      toast.error(errorMessage);
    }
  };

  const getTiffImage = async (j) => {
    console.log("tiff req begin");

    try {
      const response = await indexDataRequest.request(
        {
          id: currentIndexId,
          request_type: "tif",
          coordinates: Array.isArray(areas[j].coordinates)
            ? areas[j].coordinates
            : null,
          geo: Array.isArray(areas[j].coordinates)
            ? null
            : JSON.stringify(areas[j].coordinates),
          from_date: datePickerTimeFrom,
          to_date: datePickerTimeTo,
        },
        false
      );

      console.log("tiff res", response);

      // Can't connect to server
      if (!response) {
        toast.error(t("request_error"));
        return;
      }

      if (response.status !== 200) {
        const errorMessage = getErrorMessage(
          { response: response },
          "Failed to download TIFF file"
        );
        toast.error(errorMessage);
        return;
      }

      // Success
      dispatch(setDownloadLink(response.data));
      const link = document.createElement("a");
      link.href = response.data;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("TIFF file downloaded successfully");
    } catch (error) {
      console.error("Error fetching TIFF:", error);
      const errorMessage = getErrorMessage(
        error,
        "Failed to download TIFF file"
      );
      toast.error(errorMessage);
    }
  };
  /**
   * checks whether to fetch image from fao or our api
   * then fetch images of given indexes
   * @param  {int} polyIndexes int indexes of images to fetch
   */

  const fetchImagesAtIdx = async (...polyIndexes) => {
    try {
      // Request images from our server
      const promises = polyIndexes.map((j) => getXImage(j));
      await Promise.allSettled(promises); // Use allSettled to handle individual errors
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Failed to fetch some images");
    }
  };

  const fetchTiffImage = async (index) => {
    if (areas.length === 0) {
      toast.warn("No areas selected");
      return;
    }

    setTiffLoading(true); // Start loading
    try {
      await getTiffImage(index);
    } catch (error) {
      console.error("Error in fetchTiffImage:", error);
      const errorMessage = getErrorMessage(error, "Failed to fetch TIFF image");
      toast.error(errorMessage);
    } finally {
      setTiffLoading(false); // End loading
    }
  };
  /**
   *
   * handles the user submit action, fetches the images and stats
   */
  const handleClickSubmit = async () => {
    // TODO: check if polygon is inside arab land
    if (areas.length === 0) return;

    if (areas.length > 2) {
      toast.warn("Please select only 2 areas");
      return;
    }

    setSubmitLoading(true);
    try {
      if (areas.length === 1) {
        // Fetch data for polygon 0
        await fetchImagesAtIdx(0);
      }

      if (areas.length === 2) {
        if (JSON.stringify(chartData) === "{}") {
          // If no data exists call fetch for polygon 0 & 1
          await fetchImagesAtIdx(0, 1);
        } else {
          // Some data exists, means we need polygon 1 data
          await fetchImagesAtIdx(1);
        }
      }
    } catch (error) {
      console.error("Error in handleClickSubmit:", error);
      const errorMessage = getErrorMessage(error, "Failed to process request");
      toast.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  /**
   * controls submit button enable state,
   * @returns boolean value to disable or enable button
   */
  const disableBtn = () => {
    if (submitLoading) return true;
    if (!datePickerTimeFrom || !datePickerTimeTo) return true;
    if (dateRangeError) return true;
    if (areas.length === 0) return true;
    if (areas.length > 2) return true;
    if (chartData?.series?.length >= 2) return true;
    return false;
  };

  const getChartDataMemoized = useMemo(
    () => () => getChartData(dataKey, loggedUserState, areas),
    [areas, dataKey, loggedUserState]
  );
  const chartData = getChartDataMemoized();

  return (
    <>
      <div className="monitoring-container">
        <div className="map-wrapper">
          {!isMobile && (
            <Stripe>
              <NavPageSelector />
              <div className="stripe-content">
                <LanguageSwitcher stripe={true} />
                <SignInComponent
                  menuIconColor="black"
                  backgroundColor="white"
                  classN="modalcontainer-base"
                  stripe={true}
                />
              </div>
            </Stripe>
          )}
          {
            <Stripe second={true}>
              <CoordinatesInput />
              {dataKey && (
                <>
                  <div className="second-stripe-btns">
                    {isAdmin && (
                      <button
                        className="submit-compare-buttons"
                        onClick={handleOpen}
                      >
                        {t("Map.monitoring")}
                      </button>
                    )}

                    <button
                      className="submit-compare-buttons"
                      onClick={handleIndexInfoOpen}
                    >
                      {t("Map.index_info")}
                    </button>
                    {isAdmin && (
                      <button
                        className="submit-compare-buttons"
                        onClick={handleAddTiffOpen}
                      >
                        {t("Map.add_tiff")}
                      </button>
                    )}
                  </div>
                </>
              )}
              {dataKey && (
                <div className="stripe-select-date" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                  {/* Label and availability text - above the input */}
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
                    <p className="dateParagraph" style={{ margin: 0, marginBottom: '4px' }}>{t("Map.select_date")}</p>
                    {indexMinDate && indexMaxDate && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: dateRangeError ? 'error.main' : 'text.secondary',
                          fontSize: '0.7rem',
                          fontStyle: 'italic'
                        }}
                      >
                        {formatAvailableDateRange()}
                      </Typography>
                    )}
                  </div>

                  {/* Date picker and submit button row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <CustomProvider
                        locale={i18n.language === "ar" ? arEG : undefined}
                        rtl
                      >
                        <DateRangePicker
                          size={isMobile ? "sm" : "default"}
                          placement="autoVerticalStart"
                          showOneCalendar={isMobile}
                          cleanable
                          appearance={isMobile ? "subtle" : "default"}
                          value={
                            datePickerTimeFrom && datePickerTimeTo
                              ? [new Date(datePickerTimeFrom), new Date(datePickerTimeTo)]
                              : null
                          }
                          // Open calendar at last available date instead of today
                          defaultCalendarValue={indexMaxDate ? [indexMaxDate, indexMaxDate] : undefined}
                          // Restrict selectable dates to available range
                          shouldDisableDate={(date) => {
                            if (!indexMinDate || !indexMaxDate) return false;
                            return date < indexMinDate || date > indexMaxDate;
                          }}
                          // Add error styling when dates are out of range
                          style={{
                            borderColor: dateRangeError ? '#d32f2f' : undefined,
                            '--rs-input-border': dateRangeError ? '#d32f2f' : undefined
                          }}
                          className={dateRangeError ? 'date-range-error' : ''}
                          onOk={(date) => {
                            const fromDate = new Date(date[0]);
                            const toDate = new Date(date[1]);
                            fromDate.setHours(12, 0, 0, 0);
                            toDate.setHours(12, 0, 0, 0);
                            dispatch(setDatePickerFrom(fromDate.toISOString().split("T")[0]));
                            dispatch(setDatePickerTo(toDate.toISOString().split("T")[0]));
                          }}
                          onClean={() => dispatch(clearDates())}
                        />
                      </CustomProvider>
                      {/* Error message below the date picker */}
                      {dateRangeError && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'error.main',
                            fontSize: '0.7rem',
                            marginTop: '2px'
                          }}
                        >
                          {t("Map.date_out_of_range") || "Selected dates are out of range"}
                        </Typography>
                      )}
                    </div>
                    <button
                      onClick={handleClickSubmit}
                      className="submit-compare-buttons"
                      disabled={disableBtn()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        minWidth: '100px'
                      }}
                    >
                      {submitLoading ? (
                        <>
                          <CircularProgress size={16} color="inherit" />
                          <span>{t("loading")}</span>
                        </>
                      ) : (
                        t("submit")
                      )}
                    </button>
                  </div>
                </div>
              )}
            </Stripe>
          }

          {isMobile && <NavPageSelector />}
          <Map
            legendData={legendData}
            index={dataKey}
            hideButtons={true}
            showEditControl={true}
          />
        </div>
      </div>

      {dataKey && (
        <div className="submit-compare-buttons-container button-container">
          <IndexInfoDialog
            open={indexInfoOpen}
            index={indexData}
            onSave={updateIndex}
            onClose={() => setIndexInfoOpen(false)}
          />
          <AddTiffDialog
            open={addTiffOpen}
            index={indexData}
            onClose={() => setAddTiffOpen(false)}
          />
          <LandDegradationDialog
            mainIndex={mainIndex}
            open={landDegradationOpen}
            onClose={() => setLandDegradationOpen(false)}
          />
          <MonitoringDialog
            isSingleIndex={true}
            currentIndexId={currentIndexId}
            requestSource={"monitoring"}
            open={open}
            handleClose={handleClose}
          />
        </div>
      )}

      {/* Right-side panel: buttons + image info */}
      {areas && areas.length > 0 && (
        <RightSidePanel
          areas={areas}
          showOverlay={showOverlay}
          dispatch={dispatch}
          t={t}
          fetchTiffImage={fetchTiffImage}
          tiffLoading={tiffLoading}
        />
      )}

    </>
  );
}
